import type { Empresa } from '@/lib/types'

/**
 * Bloque "Datos para Transferencia Bancaria" de cotizaciones y facturas.
 *
 * La cuenta se elige por la moneda del documento: un abono en soles a la cuenta
 * en dólares lo convierte el banco al tipo de cambio del día y llega
 * descuadrado, así que no son intercambiables. El SWIFT acompaña sólo al cobro
 * en dólares, que es el único que puede venir del exterior.
 *
 * Fuente única: este bloque vivía copiado en VerCotizacion, DetalleCotTareas y
 * PortalCliente, y el rótulo "Dólares" estaba fijo en los seis sitios.
 * El PDF tiene su propia copia en `api/src/templates/cotizacion/index.hbs`
 * (Handlebars, server-side) — si cambia la lógica, cambia en ambos.
 */
export function DatosBancarios({
  empresa,
  moneda,
  ocultarCuentas = false,
}: {
  empresa: Empresa
  moneda?: string | null
  /** Cotizaciones white-label: se muestra el encabezado sin los números. */
  ocultarCuentas?: boolean
}) {
  const esSoles = moneda === 'PEN'
  const valor = (v: string | null | undefined) => (ocultarCuentas ? '' : v || '')

  return (
    <div className="cot-payment-box">
      <h4>Datos para Transferencia Bancaria</h4>
      <div className="cot-bank-info">
        <div className="cot-bank-section">
          <div className="cot-bank-label">Banco</div>
          <div className="cot-bank-value">{empresa.banco || 'Interbank'}</div>
        </div>
        <div className="cot-bank-section">
          <div className="cot-bank-label">Beneficiario</div>
          <div className="cot-bank-value">{empresa.razon_social || ''}</div>
        </div>
        <div className="cot-bank-section">
          <div className="cot-bank-label">RUC</div>
          <div className="cot-bank-value">{empresa.ruc || ''}</div>
        </div>

        {esSoles ? (
          <>
            {empresa.cuenta_corriente_pen && (
              <>
                <div className="cot-bank-divider" />
                <div className="cot-bank-section">
                  <div className="cot-bank-sublabel">Depósito Nacional (PEN)</div>
                  <div className="cot-bank-label">Cta. Corriente Soles</div>
                  <div className="cot-bank-value cot-bank-mono">{valor(empresa.cuenta_corriente_pen)}</div>
                </div>
              </>
            )}
            {empresa.cci_pen && (
              <>
                <div className="cot-bank-divider" />
                <div className="cot-bank-section">
                  <div className="cot-bank-sublabel">Transferencia Interbancaria (CCI)</div>
                  <div className="cot-bank-label">Código CCI (Soles)</div>
                  <div className="cot-bank-value cot-bank-mono">{valor(empresa.cci_pen)}</div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {empresa.cuenta_corriente && (
              <>
                <div className="cot-bank-divider" />
                <div className="cot-bank-section">
                  <div className="cot-bank-sublabel">Depósito Nacional (USD)</div>
                  <div className="cot-bank-label">Cta. Corriente Dólares</div>
                  <div className="cot-bank-value cot-bank-mono">{valor(empresa.cuenta_corriente)}</div>
                </div>
              </>
            )}
            {empresa.cci && (
              <>
                <div className="cot-bank-divider" />
                <div className="cot-bank-section">
                  <div className="cot-bank-sublabel">Transferencia Interbancaria (CCI)</div>
                  <div className="cot-bank-label">Código CCI (Dólares)</div>
                  <div className="cot-bank-value cot-bank-mono">{valor(empresa.cci)}</div>
                </div>
              </>
            )}
            {empresa.swift && (
              <>
                <div className="cot-bank-divider" />
                <div className="cot-bank-section">
                  <div className="cot-bank-sublabel">Transferencia Internacional (SWIFT)</div>
                  <div className="cot-bank-label">Código SWIFT/BIC</div>
                  <div className="cot-bank-value cot-bank-mono">{valor(empresa.swift)}</div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
